import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useJobRoleDelete, useJobRoleSave } from "@/queries/use-job-roles.query.ts";
import { ApiJobRole } from "@/types/job-role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  ConfirmationModal,
  Input,
  Modal,
  ModalProps,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

export interface JobRoleEditModalProps extends ModalProps {
  role?: ApiJobRole;
}

export function JobRoleEditModal({ role, ...props }: JobRoleEditModalProps) {
  const { id } = { ...props };
  const { t } = useTranslation();
  const { close } = useModals();
  const modalsService = useModals();
  const navigate = useNavigate();

  const isEdit = !!role?.id;

  const roleSchema = z.object({
    title: z.string().min(1, i18n.t("errors:field_required")),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(roleSchema),
    defaultValues: {
      title: role?.title ?? "",
    },
  });

  const roleSave = useJobRoleSave(control);
  const roleDelete = useJobRoleDelete();

  const onSubmit = handleSubmit(async (data) => {
    const result = await roleSave.mutateAsync({
      id: role?.id ?? 0,
      payload: {
        title: data.title,
      },
    });

    if (role) {
      analyticsService.trackEvent(analyticEvents.jobCatalog.roleEdited);
    } else {
      analyticsService.trackEvent(analyticEvents.jobCatalog.roleCreated);
    }

    close(id);

    if (!isEdit && result) {
      navigate(`/settings/jobs/roles/${result.id}/edit`);
    }
  });

  const handleDelete = () => {
    if (!role) return;

    modalsService.openModal(ConfirmationModal, {
      question: t("core:delete_job_role_title"),
      text: t("core:delete_job_role_description"),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await roleDelete.mutateAsync(role.id);
        navigate("/settings/jobs/framework", { replace: true });
        close(id);
      },
    });
  };

  return (
    <Modal
      {...props}
      title={t(isEdit ? "core:edit_job_role" : "core:create_job_role")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={roleSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Stack>
            <Input
              label={t("common:title")}
              required={true}
              autoFocus={true}
              type="text"
              {...register("title")}
              error={errors.title?.message}
            />
          </Stack>

          {isEdit && (
            <DeleteListItem title={t("core:delete_job_role_title")} onDelete={handleDelete} />
          )}
        </Stack>
      </form>
    </Modal>
  );
}
